// What this Test's list of genes is, said and changed OUTSIDE a report review (TGCLOUD-1868 review;
// plan B2, decisions 13 and 14; hazard 1868-H4).
//
// The gene list state (`genesTestedList.status`: every gene the laboratory analysed, only some, or
// no list captured) was drawn only on the gene list card of a report review. The save checks it on
// EVERY save (testRecord.js): a "complete" list that lost a gene, or a Test made an exome, was
// refused, and the refusal named a choice nothing on the plain Test modal offered, so the only ways
// out were to put the gene back or to upload the report again. Here the state is drawn in "Genes
// tested" whenever the Test has one or lists a gene, with the card's own options rule
// (verificationGroups.listStateOptions: never "complete" for an exome or a genome, nor with fewer
// genes than the report states, nor with none) and no default. Below it, what a gene the Test does
// not list reads as (testRecordModel.unlistedGeneStateOf, the server's rule): "not tested" only from
// a complete list.
//
// A read-only member is told the state, and changes nothing.
import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import {
  GENES_TESTED_LIST_LABELS,
  UNLISTED_GENE_MEANING,
  unlistedGeneStateOf
} from './testRecordModel';
import {
  completeNotOffered,
  listStateOptions
} from 'src/components/lab-report-verification/verificationGroups';
import {
  COMPLETE_NOT_OFFERED_FOR_SCOPE,
  LIST_STATE_QUESTION
} from 'src/components/lab-report-verification/labReportVerificationVocabulary';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export const LIST_STATE_NOT_SAID = 'Not said';

/**
 * @param {object} props
 * @param {object} props.test the Test as the form holds it (genesTested, genesTestedList, testScope)
 * @param {number|null} [props.statedCount] how many genes the applied report states (the Test as
 *   saved: the save keeps the stored count whatever the form sends)
 * @param {boolean} [props.readOnly]
 * @param {(status: string) => void} props.onChange
 */
export default function GeneListStateField({
  test,
  statedCount = null,
  readOnly = false,
  onChange
}) {
  const t = useAutoTranslation();

  const id = React.useId();
  const rowCount = Array.isArray(test?.genesTested)
    ? test.genesTested.length
    : 0;
  const status = test?.genesTestedList?.status || null;
  if (!status && rowCount === 0) return null;
  const count = Number.isInteger(statedCount) ? statedCount : null;
  // Each option's reason is said in `t`; its label is the English key, translated below.
  const options = listStateOptions({
    testScope: test?.testScope,
    rowCount,
    statedCount: count,
    t
  });
  const unlisted = unlistedGeneStateOf({
    ...test,
    genesTestedList: status
      ? { status, ...(count !== null ? { statedCount: count } : {}) }
      : null
  });
  const meaning = (
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', mt: 0.5 }}
      data-testid="unlisted-gene-meaning"
    >
      {t(UNLISTED_GENE_MEANING[unlisted])}
    </Typography>
  );

  if (readOnly) {
    return (
      <Box data-testid="gene-list-state">
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 600 }}>
            {t(LIST_STATE_QUESTION)}
          </Box>{' '}
          {status
            ? t(GENES_TESTED_LIST_LABELS[status])
            : t(LIST_STATE_NOT_SAID)}
        </Typography>
        {meaning}
      </Box>
    );
  }
  return (
    <Box data-testid="gene-list-state">
      <FormControl component="fieldset">
        <FormLabel
          component="legend"
          id={id}
          sx={{ fontSize: 13, fontWeight: 600 }}
        >
          {t(LIST_STATE_QUESTION)}
        </FormLabel>
        <RadioGroup
          aria-labelledby={id}
          value={options.some((o) => o.value === status) ? status : ''}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => (
            <Box key={o.value}>
              <FormControlLabel
                value={o.value}
                disabled={o.disabled}
                control={<Radio size="small" />}
                label={t(o.label)}
              />
              {o.reason && (
                <FormHelperText sx={{ mt: -0.5, ml: 4 }}>
                  {o.reason}
                </FormHelperText>
              )}
            </Box>
          ))}
        </RadioGroup>
        {completeNotOffered(test?.testScope) && (
          <FormHelperText>{t(COMPLETE_NOT_OFFERED_FOR_SCOPE)}</FormHelperText>
        )}
      </FormControl>
      {meaning}
    </Box>
  );
}
